import Vue from 'vue';
import Vuex from 'vuex';

import Requests from '@/utils/Requests';
import storagePlugin from './storage';

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    requests: undefined,
    site: undefined,
    siteId: undefined,
    siteInformation: undefined,
    contacts: undefined,
    alert: { show: false, message: undefined, type: undefined },
    autoLogin: false,
    role: undefined,
  },
  getters: {
    isLogined(state) {
      return state.requests && state.requests.accessToken;
    },
    isAdminLogined(state) {
      return state.requests && state.requests.adminToken;
    },
    isSuperuserLogined(state) {
      return state.requests && state.requests.adminToken && state.role === 'superuser';
    },
    hasSiteId(state) {
      return state.siteId !== undefined;
    },
    getRole(state) {
      return state.role;
    },
  },
  mutations: {
    login(state, requests) {
      state.requests = requests;
    },
    logout(state) {
      if (!state.requests.adminToken) {
        state.requests = undefined;
      }
      state.site = undefined;
      state.siteId = undefined;
      state.siteInformation = undefined;
      state.contacts = undefined;
    },
    setSite(state, site) {
      state.site = site;
      state.siteId = site.id;
    },
    setSiteInformation(state, siteInformation) {
      state.siteInformation = siteInformation;
    },
    setContacts(state, contacts) {
      state.contacts = contacts;
    },
    showAlert(state, { message, type }) {
      state.alert.message = message;
      state.alert.type = type;
      state.alert.show = true;
    },
    setAutoLogin(state, newAutoLogin) {
      state.autoLogin = newAutoLogin;
    },
    setRole(state, role) {
      state.role = role;
    },
  },
  actions: {
    async login({ state, getters, commit, dispatch }, { identifier, password }) {
      if (getters.isLogined) {
        await dispatch('logout');
      }
      const requests = state.requests || new Requests();
      const result = await requests.login(identifier, password);
      if (result) {
        commit('login', requests);
      }
      return result;
    },
    async logout({ state: { requests }, commit }) {
      const result = await requests.logout();
      commit('logout');
      return result;
    },
    async setSite({ state: { requests, siteId }, getters, commit }) {
      if (!getters.isLogined) {
        return undefined;
      }
      const sites = await requests.getSites();
      if (!sites) {
        return undefined;
      }
      if (sites.length > 0) {
        const site = siteId ? sites.find(s => s.id === siteId) || sites[0] : sites[0];
        const modifiedSite = {
          ...site,
          contact_use: !!site.contact_use,
        };
        commit('setSite', modifiedSite);
        return true;
      }
      return false;
    },
    async setSiteInformation({
      state: { requests, siteId },
      getters: { isLogined, hasSiteId },
      commit,
    }) {
      if (!isLogined || !hasSiteId) {
        return undefined;
      }
      const siteInfo = await requests.getSiteInformation(siteId);
      if (!siteInfo) {
        return undefined;
      }
      const modifiedSiteInfo = siteInfo.map(tabInfo => ({
        ...tabInfo,
        informations: tabInfo.informations.map((info) => {
          if (['select-option', 'color-theme'].indexOf(info.editor) === -1) {
            return info;
          }
          const options = JSON.parse(info.options);
          return { ...info, options };
        }),
      }));
      commit('setSiteInformation', modifiedSiteInfo);
      return true;
    },
    async postSiteInformation({
      state: { requests, siteId, siteInformation },
      getters: { isLogined, hasSiteId },
    }) {
      if (!isLogined || !hasSiteId || !siteInformation) {
        return undefined;
      }
      const postInfo = siteInformation.map(
        tabInfo =>
          tabInfo.informations.map((info) => {
            if (info.editor === 'image') {
              return {
                m_information_id: info.m_information_id,
                s3_file_id: info.s3_file_id,
              };
            }
            return {
              m_information_id: info.m_information_id,
              content: info.content,
            };
          }))
        .reduce((a, b) => a.concat(b))
        .filter((info) => {
          if (info.content !== undefined) {
            return true;
          } else if (info.s3_file_id !== undefined) {
            return true;
          }
          return false;
        });
      const result = await requests.postSiteInformation(siteId, postInfo);
      if (result === undefined) {
        return undefined;
      } else if (result !== true) {
        return result;
      }
      return true;
    },
    async setContacts({
      state: { requests, siteId },
      getters: { isLogined, hasSiteId },
      commit,
    }) {
      if (!isLogined || !hasSiteId) {
        return undefined;
      }
      const contacts = await requests.getContacts(siteId);
      if (!contacts) {
        return undefined;
      }
      commit('setContacts', contacts);
      return true;
    },
    async postContactItems({
      state: { requests, site, siteId },
      getters: { isLogined, hasSiteId },
    }) {
      if (!isLogined || !hasSiteId || !site || !site.site_contact_items) {
        return undefined;
      }
      const postContactItems = site.site_contact_items.map(
        sci => ({
          m_contact_item_id: sci.m_contact_item_id,
          is_used: sci.is_used,
          is_required: sci.is_required,
        }));
      const payload = {
        site_contact_items: postContactItems,
        contact_email_address: site.contact_email_address,
        contact_use: site.contact_use,
      };
      const result = await requests.postContactItems(siteId, payload);
      if (result === undefined) {
        return undefined;
      }
      return result.data;
    },
    async createSite(
      { state: { requests }, getters: { isLogined }, commit },
      { host, templateId }) {
      if (!isLogined) {
        return undefined;
      }
      const site = await requests.createSite(host, templateId);
      if (!site) {
        return undefined;
      }
      const modifiedSite = {
        ...site,
        contact_use: !!site.contact_use,
      };
      commit('setSite', modifiedSite);
      return true;
    },
    async changeTemplate({
      state: { requests, siteId },
      getters: { isLogined, hasSiteId },
      commit,
    }, { templateId }) {
      if (!isLogined || !hasSiteId) {
        return undefined;
      }
      const site = await requests.changeTemplate(siteId, templateId);
      if (!site) {
        return undefined;
      }
      const modifiedSite = {
        ...site,
        contact_use: !!site.contact_use,
      };
      commit('setSite', modifiedSite);
      return true;
    },
    async changeHost({
      state: { requests, siteId },
      getters: { isLogined, hasSiteId },
      commit,
    }, { host }) {
      if (!isLogined || !hasSiteId) {
        return undefined;
      }
      const site = await requests.changeHost(siteId, host);
      if (!site) {
        return undefined;
      }
      const modifiedSite = {
        ...site,
        contact_use: !!site.contact_use,
      };
      commit('setSite', modifiedSite);
      return true;
    },
    async changeIndustries({
      state: { requests, siteId },
      getters: { isLogined, hasSiteId },
      commit,
    }, { industries }) {
      if (!isLogined || !hasSiteId) {
        return undefined;
      }
      const site = await requests.changeIndustries(siteId, industries);
      if (!site) {
        return undefined;
      }
      const modifiedSite = {
        ...site,
        contact_use: !!site.contact_use,
      };
      commit('setSite', modifiedSite);
      return true;
    },
    async changeOpenStatus({ state: { site }, dispatch }) {
      if (!site) {
        return undefined;
      }
      return dispatch('setOpenStatus', !site.is_opened);
    },
    async setOpenStatus({
      state: { requests, siteId },
      getters: { isLogined, hasSiteId },
      commit,
    }, isOpen) {
      if (!isLogined || !hasSiteId) {
        return undefined;
      }
      const newSite = await requests.changeOpenStatus(siteId, isOpen);
      if (!newSite) {
        return undefined;
      }
      const modifiedSite = {
        ...newSite,
        contact_use: !!newSite.contact_use,
      };
      commit('setSite', modifiedSite);
      return true;
    },
    showAlert({ commit }, { message, type }) {
      if (['success', 'info', 'warning', 'error'].indexOf(type) === -1) {
        return false;
      }
      commit('showAlert', { message, type });
      return true;
    },
    async adminLogin({ state, getters, commit, dispatch }, { identifier, password }) {
      if (getters.isLogined) {
        await dispatch('logout');
      }
      const requests = state.requests || new Requests();
      const result = await requests.adminLogin(identifier, password);
      if (result) {
        commit('login', requests);
      }
      return result;
    },
    async adminLogout({ state: { requests }, commit }) {
      const result = await requests.adminLogout();
      commit('logout');
      return result;
    },
    async setAutoLogin({ commit }, newAutoLogin) {
      commit('setAutoLogin', newAutoLogin);
    },
    async setAdminRole({ state: { requests }, commit }) {
      const role = await requests.getAdminRole();
      commit('setRole', role);
      return role;
    },
  },
  plugins: [storagePlugin],
});



// WEBPACK FOOTER //
// ./src/store/index.js